<template>
    <v-container fluid class="pa-6">
        <v-card elevation="2" class="activity-card">
            <v-card-title class="page-header">
                <div>
                    <div class="page-title">
                        <v-icon color="primary" large class="mr-3">mdi-history</v-icon>
                        User Activity Log
                    </div>
                    <div class="page-subtitle">Track user actions and system events</div>
                </div>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined @click="getUserActivity()" :loading="loading" class="refresh-btn">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                </v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <v-skeleton-loader type="table" v-if="loading && user_activities.length === 0"></v-skeleton-loader>
            <v-card-text v-else class="pa-0">
                <v-data-table :headers="headers" :items="user_activities" :options.sync="options"
                    :server-items-length="total_items" :loading="loading" :footer-props="{
                        'items-per-page-options': [5, 10, 25, 50]
                    }" class="activity-table">
                    <template v-slot:item.created_at="{ item }">
                        <div class="timestamp-cell">
                            <div class="date-text">{{ $date(item.created_at).format('MMM DD, YYYY') }}</div>
                            <div class="time-text">{{ $date(item.created_at).format('h:mm A') }}</div>
                        </div>
                    </template>

                    <template v-slot:item.user="{ item }">
                        <div class="user-cell">
                            <v-avatar size="32" color="primary" class="mr-2">
                                <span class="white--text text-subtitle-2">{{ getUserInitials(item.user?.name) }}</span>
                            </v-avatar>
                            <div>
                                <div class="user-name">{{ item.user?.name }}</div>
                                <div class="user-id">ID: {{ item.user_id }}</div>
                            </div>
                        </div>
                    </template>

                    <template v-slot:item.company="{ item }">
                        <div>
                            <v-chip small color="success" outlined class="company-chip">
                                <v-icon small left>mdi-domain</v-icon>
                                {{ item.company?.company_name }}
                            </v-chip>
                            <div class="company-id">ID: {{ item.company_id }}</div>
                        </div>
                    </template>

                    <template v-slot:item.message="{ item }">
                        <div class="message-cell">{{ item.message }}</div>
                    </template>

                    <template v-slot:item.details="{ item }">
                        <div class="details-cell">
                            <v-chip v-if="item.lead_id" small color="blue" text-color="white" class="ma-1">
                                <v-icon small left>mdi-account</v-icon>
                                Lead: {{ item.lead?.first_name }} {{ item.lead?.last_name }}
                            </v-chip>

                            <v-chip v-if="item.quote_id" small color="purple" text-color="white" class="ma-1">
                                <v-icon small left>mdi-file-document</v-icon>
                                Quote: {{ item.quote?.name }}
                            </v-chip>

                            <v-chip v-if="item.event_id" small color="orange" text-color="white" class="ma-1">
                                <v-icon small left>mdi-calendar</v-icon>
                                Event: {{ item.event?.name }}
                            </v-chip>

                            <v-chip v-if="item.invitee_id" small color="teal" text-color="white" class="ma-1">
                                <v-icon small left>mdi-account-group</v-icon>
                                Invitee: {{ item.invitee?.name }}
                            </v-chip>

                            <v-chip v-if="item.supplier_id" small color="indigo" text-color="white" class="ma-1">
                                <v-icon small left>mdi-store</v-icon>
                                Supplier: {{ item.supplier?.supplier_name }}
                            </v-chip>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
export default {
    components: {
    },
    props: [
    ],
    data() {
        return {
            loading: false,
            headers: [
                { text: 'Timestamp', value: 'created_at', width: '150px', sortable: false },
                { text: 'User', value: 'user', width: '200px', sortable: false },
                { text: 'Company', value: 'company', width: '200px', sortable: false },
                { text: 'Description', value: 'message', sortable: false },
                { text: 'Related Items', value: 'details', sortable: false },
            ],
            user_activities: [],
            total_items: 0,
            options: {
                page: 1,
                itemsPerPage: 10
            },
        }
    },
    async mounted() {
        // Data will load via options watcher
    },
    created() {
        this.setPageName('User Activity')
        this.getUserActivity()
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        })
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction'
        }),
        getUserInitials(name) {
            if (!name) return '??'
            const parts = name.trim().split(' ')
            if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        },
        showSnackBar(message) {
            this.$store.commit("auth/setMessage",
                { show: true, message: message },
                { root: 1 })
        },
        async getUserActivity() {
            this.loading = true
            const { page, itemsPerPage } = this.options

            this.$axios.get('admin/user_activity/get_user_activities', {
                params: {
                    page: page,
                    per_page: itemsPerPage
                }
            })
                .then(({ data }) => {
                    if (data.response) {
                        this.user_activities = data.data
                        this.total_items = data.meta.total
                        this.loading = false
                    }
                })
                .catch(() => {
                    this.loading = false
                })
        },
    },
}
</script>

<style scoped lang="scss">
.activity-card {
    border-radius: 8px !important;
    overflow: hidden;
}

.page-header {
    padding: 24px !important;
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
}

.page-title {
    font-family: Manrope, sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.page-subtitle {
    font-family: Manrope, sans-serif;
    font-size: 13px;
    color: #7f8c8d;
    font-weight: 400;
    margin-left: 48px;
}

.refresh-btn {
    text-transform: none !important;
    font-family: Manrope, sans-serif;
    font-weight: 600;
    letter-spacing: 0;
}

.activity-table {
    :deep(tbody tr) {
        &:hover {
            background-color: #f8f9fa !important;
        }
    }

    :deep(td) {
        padding: 16px !important;
        border-bottom: 1px solid #eceff1 !important;
    }

    :deep(th) {
        font-family: Manrope, sans-serif;
        font-weight: 700 !important;
        font-size: 12px !important;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #546e7a !important;
        background-color: #fafafa !important;
    }
}

.timestamp-cell {
    font-family: Manrope, sans-serif;

    .date-text {
        font-size: 14px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 2px;
    }

    .time-text {
        font-size: 12px;
        color: #95a5a6;
        font-weight: 400;
    }
}

.user-cell {
    display: flex;
    align-items: center;
    font-family: Manrope, sans-serif;

    .user-name {
        font-size: 14px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 2px;
    }

    .user-id {
        font-size: 11px;
        color: #95a5a6;
    }
}

.company-chip {
    font-family: Manrope, sans-serif;
    font-weight: 600;
    margin-bottom: 4px;
}

.company-id {
    font-size: 11px;
    color: #95a5a6;
    margin-top: 4px;
    font-family: Manrope, sans-serif;
}

.message-cell {
    font-family: Manrope, sans-serif;
    font-size: 14px;
    color: #34495e;
    line-height: 1.5;
    max-width: 300px;
}

.details-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 400px;

    :deep(.v-chip) {
        font-family: Manrope, sans-serif;
        font-weight: 500;
    }
}
</style>